/* eslint-disable @angular-eslint/prefer-inject */
import { PackingListDataService } from '../infrastructure/packing-lists/packing-list.data.service';
import {
    PackingListCreate,
    PackingListDto,
    PackingListRead,
    PackingListSearchParams,
} from '../entities/packing-list.dto';
import { PackingListStore } from '../infrastructure/packing-lists/packing-list.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { PackingListMapper } from '../infrastructure/packing-lists/packing-list.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { IQueryParams, SafeAny } from '@pf/shared-common';
import { WarehouseFacade } from './warehouse.facade';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { PackingStatus, ResourceAuth } from '@control-tower/platform-wms';
import { ProjectStore } from '../infrastructure/projects/project.store';

@Injectable()
export class PackingListFacade extends AbstractManageEntityFacade<
    PackingListDto,
    PackingListRead,
    PackingListCreate,
    PackingListSearchParams
> {
    private readonly projectStore = inject(ProjectStore);
    private readonly warehouseFacade = inject(WarehouseFacade);
    nameField: keyof PackingListDto = 'packingListNumber';

    constructor(
        dataService: PackingListDataService,
        store: PackingListStore,
        mapper: PackingListMapper
    ) {
        super(dataService, null, store, mapper);
    }

    override getDefaultSearchParams$(
        params: IQueryParams<PackingListDto>
    ): Observable<IQueryParams<PackingListDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: PackingListCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof PackingListDto, string>> {
        return { packingListNumber: searchText };
    }

    createAndClaimPackingListForProject$(projectId: string) {
        const project = this.projectStore.get(projectId);
        if (!project) {
            throw new Error('Project not found');
        }
        const obs$ = project.packingLists?.length
            ? of(project.packingLists.map(pl => pl.id as string))
            : this.save$(
                  new PackingListDto({
                      projectId,
                      status: PackingStatus.Pending,
                      packingListNumber: '1',
                      warehouse: { id: this.warehouseFacade.active!.id },
                  })
              ).pipe(map(pl => [pl.id]));
        return obs$.pipe(
            switchMap(packingListIds => {
                return (
                    this.dataService as PackingListDataService
                ).claimPackingLists$(
                    project.warehouse!.id as string,
                    packingListIds
                );
            })
        );
    }

    unClaimPackingLists$(packingListIds: string[]) {
        return (this.dataService as PackingListDataService)
            .unClaimPackingLists$(
                this.warehouseFacade.active!.id,
                packingListIds
            )
            .pipe(
                switchMap(() => {
                    return combineLatest(
                        packingListIds.map(id => this.delete$(id))
                    );
                })
            );
    }
}
